/**
 * What the plugin shows before the sandbox has answered.
 *
 * The UI is a hosted web page in an iframe; everything it can do needs the plugin sandbox on the
 * other side of `postMessage`. The handshake is one round trip — the UI emits `GET_USER_INFO` on
 * mount and the sandbox replies `USER_INFO` with the Figma user id — and until that lands there is
 * no `figmaUID`, which is what every capture path checks first.
 *
 * Without this screen the whole app rendered during that gap: tabs, a URL field, an enabled
 * Import button. Clicking it got "Figma user ID is required" — a message about an internal
 * identifier, for a state the user could not have caused and could do nothing about.
 *
 * It is deliberately NOT an unbounded spinner. A spinner with no exit is the failure this
 * repository just spent a commit removing, and "the sandbox never replied" is a real state: the
 * page can be open outside Figma, or the relay in `figma.showUI` can have gone away with a
 * reloaded plugin. So the wait is bounded and turns into something the user can act on.
 */
import { Button, Container, LoadingIndicator, Muted, Text, VerticalSpace } from "../../ui-kit";

interface PluginConnectingProps {
  /** True once the handshake has been waited out — show the failure, not the spinner. */
  timedOut: boolean;
  /** Re-send the handshake. */
  onRetry: () => void;
}

function PluginConnecting({ timedOut, onRetry }: PluginConnectingProps) {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--figma-color-bg)",
      }}
    >
      <Container space="medium">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          {timedOut ? (
            <>
              <Text>
                <strong>Can’t reach Figma</strong>
              </Text>
              <VerticalSpace space="small" />
              <Text>
                <Muted>
                  <span style={{ fontSize: "11px" }}>
                    The plugin didn’t answer. Closing and reopening it from the Plugins menu
                    usually fixes this.
                  </span>
                </Muted>
              </Text>
              <VerticalSpace space="medium" />
              <Button secondary onClick={onRetry}>
                Try again
              </Button>
            </>
          ) : (
            <>
              <LoadingIndicator />
              <VerticalSpace space="small" />
              <Text>
                <Muted>
                  <span style={{ fontSize: "11px" }}>Connecting to Figma…</span>
                </Muted>
              </Text>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}

export default PluginConnecting;
